import {axiosInstance, axiosPublicInstance} from 'config/axios';
import * as Sentry from '@sentry/react';

interface AxiosBaseQueryArgs {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  data?: unknown;
  params?: Record<string, any> | void;
  headers?: Record<string, string>;
  isPublic?: boolean;
}

// Methods that modify state - these should be tracked by Sentry
const MUTATING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Track API errors to Sentry for mutating operations
 */
const trackApiError = (err: any, url: string, method: string) => {
  if (MUTATING_METHODS.includes(method)) {
    Sentry.captureException(err, {
      tags: {
        api_error: 'true',
        http_method: method,
        http_status: err.response?.status?.toString() || 'unknown',
      },
      extra: {
        url,
        method,
        status: err.response?.status,
        errorMessage: err.response?.data?.message || err.message,
        errorData: err.response?.data,
      },
    });
  }
};

const instanceFactory = (isPublic: boolean) =>
  isPublic ? axiosPublicInstance : axiosInstance;

export const axiosBaseQueryFactory =
  (defaultConfig: {isPublic: boolean}) =>
  async ({
    url,
    method,
    data,
    params,
    headers,
    isPublic,
  }: AxiosBaseQueryArgs) => {
    try {
      // if query args are present, override
      const factoryPublic: boolean = typeof isPublic  === "undefined" ? defaultConfig.isPublic : isPublic;

      const result = await instanceFactory(factoryPublic)({
        url,
        method,
        data,
        params,
        headers,
      });

      return {data: result.data};
    } catch (err: any) {
      trackApiError(err, url, method);
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
          isPermissionError: err.isPermissionError || false,
        },
      };
    }
  };

export const axiosBaseQuery =
  () =>
  async ({url, method, data, params, headers}: AxiosBaseQueryArgs) => {
    try {
      const result = await axiosInstance({
        url,
        method,
        data,
        params,
        headers,
      });

      return {data: result.data};
    } catch (err: any) {
      trackApiError(err, url, method);
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
          isPermissionError: err.isPermissionError || false,
        },
      };
    }
  };

export const axiosBasePublicQuery =
  () =>
  async ({url, method, data, params, headers}: AxiosBaseQueryArgs) => {
    try {
      const result = await axiosPublicInstance({
        url,
        method,
        data,
        params,
        headers,
      });

      return {data: result.data};
    } catch (err: any) {
      trackApiError(err, url, method);
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
          isPermissionError: err.isPermissionError || false,
        },
      };
    }
  };
